import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { authService, User } from './services/authService';

// Layout Components
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Apps from './pages/Apps';
import Services from './pages/Services';
import Users from './pages/Users';
import Billing from './pages/Billing';
import ITAssets from './pages/ITAssets';
import Tickets from './pages/Tickets';
import Documents from './pages/Documents';
import LegacyVoice from './pages/LegacyVoice';
import SoftwareDevelopment from './pages/SoftwareDevelopment';
import SupportSessionPage from './pages/SupportSessionPage';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Check for token in URL parameters (for impersonate functionality)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    // Check if auto-login is explicitly prevented
    const preventAutoLogin = sessionStorage.getItem('preventAutoLogin') === 'true';
    
    // Only attempt auto-login if we have a token, no user, and auto-login is not prevented
    if (token && !user && !preventAutoLogin) {
      // Double-check we're not on password reset page by checking the URL
      const isPasswordResetPage = window.location.pathname.includes('page-change-password');
      
      if (!isPasswordResetPage) {
        handleTokenLogin(token);
      }
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      // If we're on the password reset page or support page, don't restore any existing session
      const isPasswordResetPage = window.location.pathname.includes('page-change-password');
      const isSupportRoute = window.location.pathname.startsWith('/support/');

      if (isSupportRoute) {
        setLoading(false);
        return;
      }

      if (isPasswordResetPage) {
        // Clear session without redirect (don't use logout() as it redirects)
        localStorage.removeItem('userDetails');
        localStorage.removeItem('impersonating');
        setUser(null);
        setLoading(false);
        return;
      }
      
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        // Check if token is still valid (not expired)
        if (currentUser.expireDate && new Date() < new Date(currentUser.expireDate)) {
          setUser(currentUser);
        } else {
          // Try to refresh token if it exists
          try {
            const refreshedUser = await authService.refresh(currentUser.access_token);
            setUser(refreshedUser);
          } catch (error) {
            console.warn('Token refresh failed, user needs to login again');
            authService.logout();
            setUser(null);
          }
        }
      } else {
        // No user in localStorage
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (username: string, password: string) => {
    try {
      const user = await authService.login(username, password);
      setUser(user);
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
  };

  const handleTokenLogin = async (token: string) => {
    try {
      setLoading(true);
      // The SSO callback lands on /login?token=...; impersonation links land
      // elsewhere (e.g. root). Only the latter is a true impersonation session.
      const isSSO = window.location.pathname.includes('/login');
      const user = await authService.loginWithToken(token, !isSSO);
      setUser(user);

      // Check for redirect parameter
      const url = new URL(window.location.href);
      const redirectPath = url.searchParams.get('redirect');

      // Remove token and redirect from URL to prevent confusion
      url.searchParams.delete('token');
      url.searchParams.delete('redirect');

      // Navigate to redirect path if provided, otherwise stay on current path
      if (redirectPath) {
        window.history.replaceState({}, document.title, redirectPath);
      } else {
        window.history.replaceState({}, document.title, url.pathname + url.search);
      }
    } catch (error: any) {
      console.error('❌ Token login failed:', error);
      // If token login fails, redirect to normal login
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has access to IT Asset Manager (admin only)
  const hasITAssetAccess = () => {
    return !!user?.auth?.admin_group;
  };

  // Check if current path is a public support session page
  const isSupportPage = window.location.pathname.startsWith('/support/');

  if (loading && !isSupportPage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Toaster position="top-right" />
        <Routes>
          {/* Public route - no auth required, always accessible */}
          <Route path="/support/:sessionId" element={<SupportSessionPage />} />

          {/* Everything else follows existing auth pattern */}
          <Route path="/*" element={
            !user ? (
              <Routes>
                <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/page-change-password" element={<ChangePasswordPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            ) : (
              <Layout user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Navigate to="/apps" replace />} />
                  <Route path="/apps" element={<Apps />} />
                  <Route path="/profile" element={<Profile user={user} />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/tickets" element={<Tickets />} />
                  <Route path="/documents" element={<Documents />} />

                  {/* Admin-only routes */}
                  {user?.auth?.admin_group && (
                    <Route path="/users" element={<Users />} />
                  )}

                  {/* Billing-only routes */}
                  {user?.auth?.billing_group && (
                    <Route path="/billing" element={<Billing />} />
                  )}

                  {/* IT Assets - admin only */}
                  {hasITAssetAccess() && (
                    <Route path="/it-assets" element={<ITAssets />} />
                  )}

                  {/* Legacy Voice Management */}
                  <Route path="/legacy-voice" element={<LegacyVoice />} />

                  {/* Software Development as a Service */}
                  <Route path="/software-development" element={<SoftwareDevelopment />} />

                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="*" element={<Navigate to="/apps" replace />} />
                </Routes>
              </Layout>
            )
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;